import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  FormControl,
  InputLabel,
  OutlinedInput,
  Snackbar,
  Typography,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import Alert from '@mui/material/Alert';
import { post } from '../../../utils/fetch';
import { ENV, SNACKBAR_DURATION } from '../../../constants';
import { useCurrentUser } from '../../../context/UserContext';
import { authenticatedPost } from '../../../utils/authenticatedFetch';

const styles = {
  main: {
    backgroundColor: '#fff',
    padding: '18px 22px',
    textAlign: 'center',
  },
  textInput: {
    margin: '20px 0',
  },
}

export default function RSVPVerification() {
  const { loadUser } = useCurrentUser();
  const [code, setCode] = useState('');
  const [token, setToken] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: 'success',
  });

  const { refetch: login } = useQuery({
    queryKey: ['login', token],
    queryFn: async () => {
      const response = await authenticatedPost(`${ENV.API_URL}/login`, {
        token,
      });

      if (!response.ok) {
        throw new Error('Login failed');
      }

      return response.json();
    },
    onSuccess: () => {
      loadUser();
    },
    enabled: false,
  });

  const { refetch: submitCode, data, isLoading } = useQuery({
    queryKey: ['verifyLogin', code],
    queryFn: async () => {
      const response = await post(`${ENV.API_URL}/urls/code/${code}`);
      if (!response.ok) {
        throw new Error('Invalid verification code');
      }
      return response.json();
    },
    enabled: false,
    onError: (error) => {
      setSnackbar({
        open: true,
        message: error?.message || 'Something happened...',
        type: 'error',
      });
    },
  });

  useEffect(() => {
    if (!isLoading && data) {
      setToken(data.data.token);
    }
  }, [data, isLoading]);

  useEffect(() => {
    if (code.length === 8) {
      submitCode();
    }
  }, [code, submitCode]);

  useEffect(() => {
    if (token) {
      login();
    }
  }, [token, login]);

  return (
    <Box sx={styles.main}>
      <ChatBubbleOutlineIcon color="secondary" style={{ fontSize: '40px' }} />
      <Typography color="primary" variant="h3">
        Verify your phone number
      </Typography>
      <Typography color="primary" variant="h5">
        You should get a text with an 8-digit code. Enter your code here
      </Typography>
      <FormControl sx={styles.textInput} variant="outlined">
        <InputLabel htmlFor="code">Code</InputLabel>
        <OutlinedInput
          id="code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          label="Code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="\d{8}"
        />
      </FormControl>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={SNACKBAR_DURATION}
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.type}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
